<template>
  <div>
    <b-card class="shadow border">
      <h-f-fields
        v-model="form.title"
        label="Titre"
        :submitted="submitted"
        :invalid="$v.form.title.$error"
        :required="!$v.form.title.required"
      />
      <h-f-fields v-model="form.description" label="Description" input-type="textarea" />
      <b-row>
        <b-col cols="6">
          <h-f-fields
            v-model="form.date"
            label="Date d'échéance"
            input-type="date"
            :submitted="submitted"
            :invalid="$v.form.date.$error"
            :required="!$v.form.date.required"
          />
        </b-col>
        <b-col cols="6">
          <h-f-model-multi-taggable
            v-model="form.assigned_user"
            label="Utilisateur assigné"
            ressource="users"
            :base-url="userUrl"
            :text-choices="['first_name', 'last_name']"
            value-choice="uuid"
          />
        </b-col>
      </b-row>
      <div v-if="!isLoadingPost" class="d-flex justify-content-between mt-3">
        <h-b-action text="Annuler" :positive="false" @click.native="refreshForm" />
        <h-b-action text="Valider" :positive="true" @click.native="submitTask" />
      </div>
      <div v-else class="d-flex justify-content-end">
        <h-e-spinner size="sm" class="justify-content-end" />
      </div>
    </b-card>
    <h-e-top-title icon="bx bx-notepad bx-sm" title="Tâches en cours" class="mt-3" />
    <h-c-task v-for="(task, index) in ongoingTasks" :key="task.id" :title="task.title" :description="task.description">
      <template #buttons>
        <div class="d-flex mr-3">
          <h-b-action-rounded
            icon="bx bx-edit-alt"
            background-white
            class="mr-2"
            @click.native="prepareEdit(index, 'ongoingTasks')"
          />
          <h-b-action-rounded
            icon="bx bx-trash-alt"
            background-white
            @click.native="deleteTask(index, 'ongoingTasks')"
          />
        </div>
      </template>
      <template #bottom>
        <div class="d-flex mb-3">
          <div v-if="task.assigned_user_object" class="d-flex">
            Assigné à : {{ task.assigned_user_object.first_name }} {{ task.assigned_user_object.last_name }}
          </div>
          <div class="d-flex">Date d'échéance : {{ task.date | formatDate }}</div>
        </div>
      </template>
      <template #bottom-buttons>
        <h-b-action-rounded
          icon="bx bx-check bx-sm"
          color="success"
          background-white
          @click.native="changeTaskStatus(index, 'ongoingTasks')"
        />
      </template>
    </h-c-task>
    <h-e-spinner v-if="isLoadingOngoing" class="justify-content-center" />
    <h-b-action-rounded
      v-if="!isLoadingOngoing && pageSize * ongoingPage < ongoingRows"
      icon="bx bx-plus"
      @click.native="getMoreOngoingTask"
    />
    <h-e-top-title icon="mdi mdi-check-circle-outline bx-sm" title="Tâches terminées" class="mt-3" />
    <h-c-task v-for="(task, index) in finishedTasks" :key="task.id" :title="task.title" :description="task.description">
      <template #buttons>
        <div class="d-flex mr-3">
          <h-b-action-rounded
            icon="bx bx-edit-alt"
            background-white
            class="mr-2"
            @click.native="prepareEdit(index, 'finishedTasks')"
          />
          <h-b-action-rounded
            icon="bx bx-trash-alt"
            background-white
            @click.native="deleteTask(index, 'finishedTasks')"
          />
        </div>
      </template>
      <template #bottom-buttons>
        <h-b-action-rounded
          icon="bx bx-check bx-sm"
          color="success"
          positive
          @click.native="changeTaskStatus(index, 'finishedTasks')"
        />
      </template>
    </h-c-task>
    <h-e-spinner v-if="isLoadingFinished" class="justify-content-center" />
    <h-b-action-rounded
      v-if="!isLoadingFinished && pageSize * finishedPage < finishedRows"
      icon="bx bx-plus"
      @click.native="getMoreFinishedTask"
    />
  </div>
</template>

<script>
import { FunctionHelpers, Hk } from '@/node-sdk'
import { required } from 'vuelidate/lib/validators'
import moment from 'moment/moment'

const { Task } = Hk.Event

export default {
  name: 'task-v2',

  filters: {
    formatDate(date) {
      return moment(date).format('LL')
    },
  },

  props: {
    externalId: {
      type: String,
      default: '',
    },
    dashboardTitle: {
      type: String,
      default: '',
    },
    link: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      form: this.getForm(),
      submitted: false,
      userUrl: process.env.VUE_APP_API,
      ongoingTasks: [],
      finishedTasks: [],
      ongoingRows: 0,
      finishedRows: 0,
      ongoingPage: 1,
      finishedPage: 1,
      isLoadingPost: false,
      isLoadingOngoing: false,
      isLoadingFinished: false,
      isEditing: false,
      editingType: '',
      editingIndex: 0,
      pageSize: 15,
    }
  },

  validations: {
    form: {
      title: { required },
      date: { required },
    },
  },

  async mounted() {
    await this.getOngoingTask()
    await this.getFinishedTask()
  },

  methods: {
    async getOngoingTask() {
      this.isLoadingOngoing = true
      ;[this.ongoingTasks, this.ongoingRows] = await Task.get({
        params: {
          external_id: this.externalId,
          finished: false,
          page_size: this.pageSize,
        },
      })
      this.isLoadingOngoing = false
    },

    async getFinishedTask() {
      this.isLoadingFinished = true
      ;[this.finishedTasks, this.finishedRows] = await Task.get({
        params: {
          external_id: this.externalId,
          finished: true,
          page_size: this.pageSize,
        },
      })
      this.isLoadingFinished = false
    },

    async getMoreOngoingTask() {
      this.isLoadingOngoing = true
      const [response] = await Task.get({
        params: {
          external_id: this.externalId,
          finished: false,
          page: this.ongoingPage,
          page_size: this.pageSize,
        },
      })
      this.ongoingPage += 1
      this.ongoingTasks = [...this.ongoingTasks, ...response]
      this.isLoadingOngoing = false
    },

    async getMoreFinishedTask() {
      this.isLoadingFinished = true
      const [response] = await Task.get({
        params: {
          external_id: this.externalId,
          finished: true,
          page: this.finishedPage,
          page_size: this.pageSize,
        },
      })
      this.finishedPage += 1
      this.finishedTasks = [...this.finishedTasks, ...response]
      this.isLoadingFinished = false
    },

    async submitTask() {
      this.submitted = true
      this.$v.$touch()
      if (this.$v.$invalid) {
        FunctionHelpers.formErrorModal()
      } else {
        this.isLoadingPost = true
        if (!this.isEditing) {
          const response = await Task.create({
            ...this.form,
            external_id: this.externalId,
          })
          this.ongoingTasks.push(response)
        } else {
          const response = await Task.patch(this.form.id, {
            data: this.form,
          })
          this.$set(this[this.editingType], this.editingIndex, response)
        }
        this.form = this.getForm()
        this.submitted = false
        this.isLoadingPost = false
      }
    },

    async deleteTask(index, type) {
      await Task.delete(this[type][index].id)
      this.$delete(this[type], index)
    },

    prepareEdit(index, type) {
      this.isEditing = true
      this.editingType = type
      this.editingIndex = index
      this.form = this[type][index]
    },

    refreshForm() {
      this.form = this.getForm()
      this.isEditing = false
      this.editingType = ''
      this.editingIndex = 0
    },

    changeTaskStatus(index, type) {
      this[type][index].finished = !this[type][index].finished
      Task.patch(this[type][index].id, {
        data: this[type][index],
      })
      this[type === 'ongoingTasks' ? 'finishedTasks' : 'ongoingTasks'].push(this[type][index])
      this.$delete(this[type], index)
    },

    getForm() {
      return {
        ...Task.get_form(),
        assigned_user: JSON.parse(localStorage.getItem('user_object')).uuid,
        dashboard_title: this.dashboardTitle,
        link: this.link,
      }
    },
  },
}
</script>

<style scoped></style>
